import { useMemo } from 'react'
import { getCommentsTabForComment, getCommentsTabForDataType } from './CommentsView.helpers'
import {
    CommentsViewDomainTab,
    CommentsViewDomainTabId,
    CommentsViewNavigationTarget,
    CommentsViewTab,
} from './CommentsView.types'
import { singular } from 'pluralize'
const VALUE_NARRATIVES_PATH = '/narratives'

const getAutoOpenTarget = (
    pathname: string,
    autoOpenData: Record<string, unknown>,
    previousPath: string
): CommentsViewNavigationTarget => ({
    pathname,
    autoOpenData: {
        isAutoOpenPanel: true,
        autoOpenData,
        previousDataStack: [{ path: previousPath, data: {} }],
    },
})

type DisplayName = { singular: string; plural: string }

type UseCommentsViewTabsArgs = {
    strategicImperativeDisplayName: DisplayName
    valueMessageDisplayName: DisplayName
    valueNarrativeDisplayName: DisplayName
    activitiesDisplayName: DisplayName
    evidenceDisplayName: DisplayName
    tppAcronymDisplayName: string
}

type UseCommentsViewTabsResult = {
    tabs: CommentsViewTab[]
    domainTabLabels: Record<CommentsViewDomainTabId, string>
}

export const useCommentsViewTabs = ({
    strategicImperativeDisplayName,
    valueMessageDisplayName,
    valueNarrativeDisplayName,
    activitiesDisplayName,
    evidenceDisplayName,
    tppAcronymDisplayName,
}: UseCommentsViewTabsArgs): UseCommentsViewTabsResult => {
    const domainTabs = useMemo(
        (): CommentsViewDomainTab[] => [
            {
                id: 'strategicImperative',
                label: strategicImperativeDisplayName.plural,
                emptyStateTitle: `No ${strategicImperativeDisplayName.plural.toLowerCase()} comments yet`,
                emptyStateSubtitle: 'Comments added from the table or detail panel will appear here.',
                getTypeLabel: (dataType) =>
                    dataType === 'strategicImperativeTable'
                        ? `${strategicImperativeDisplayName.plural} Table`
                        : strategicImperativeDisplayName.singular,
                getLocationLabel: (comment) =>
                    comment.location.type === 'table'
                        ? `${strategicImperativeDisplayName.plural} Table`
                        : (comment.location.name ?? 'Deleted'),
                getLocationTarget: (comment, currentPath) => {
                    if (comment.location.type === 'table') return { pathname: '/strategic-imperatives' }
                    if (comment.location.type !== 'item' || !comment.location.id) return null
                    return getAutoOpenTarget('/strategic-imperatives', { _id: comment.location.id }, currentPath)
                },
            },
            {
                id: 'valueMessage',
                label: valueMessageDisplayName.plural,
                emptyStateTitle: `No ${valueMessageDisplayName.plural.toLowerCase()} comments yet`,
                emptyStateSubtitle: 'Comments added from the table, narratives, or messages will appear here.',
                getTypeLabel: (dataType) => {
                    if (dataType === 'valueMessageTable') return `${valueNarrativeDisplayName.plural} Table`
                    if (dataType === 'valueNarrative') return valueNarrativeDisplayName.singular
                    return valueMessageDisplayName.singular
                },
                getLocationLabel: (comment) =>
                    comment.location.type === 'table'
                        ? `${valueMessageDisplayName.plural} Table`
                        : (comment.location.name ?? 'Deleted'),
                getLocationTarget: (comment, currentPath) => {
                    if (comment.location.type === 'table') return { pathname: VALUE_NARRATIVES_PATH }
                    if (comment.location.type !== 'item' || !comment.location.id) return null
                    if (comment.dataType === 'valueNarrative') {
                        const categoryId = comment.extras?.categoryId
                        if (!categoryId) return { pathname: VALUE_NARRATIVES_PATH }
                        return {
                            pathname: `${VALUE_NARRATIVES_PATH}/categories/${categoryId}/narratives/${comment.location.id}`,
                        }
                    }
                    return getAutoOpenTarget(VALUE_NARRATIVES_PATH, { _id: comment.location.id }, currentPath)
                },
            },
            {
                id: 'activity',
                label: activitiesDisplayName.plural,
                emptyStateTitle: `No ${activitiesDisplayName.plural.toLowerCase()} comments yet`,
                emptyStateSubtitle: 'Comments added from the table or detail panel will appear here.',
                getTypeLabel: (dataType) =>
                    dataType === 'activityTable'
                        ? `${activitiesDisplayName.plural} Table`
                        : activitiesDisplayName.singular,
                getLocationLabel: (comment) =>
                    comment.location.type === 'table'
                        ? `${activitiesDisplayName.plural} Table`
                        : (comment.location.name ?? 'Deleted'),
                getLocationTarget: (comment, currentPath) => {
                    if (comment.location.type === 'table') return { pathname: '/activities' }
                    if (comment.location.type !== 'item' || !comment.location.id) return null
                    return getAutoOpenTarget('/activities', { _id: comment.location.id }, currentPath)
                },
            },
            {
                id: 'evidence',
                label: evidenceDisplayName.plural,
                emptyStateTitle: `No ${evidenceDisplayName.plural.toLowerCase()} comments yet`,
                emptyStateSubtitle: 'Comments added from the table or detail panel will appear here.',
                getTypeLabel: (dataType) =>
                    dataType === 'evidenceTable' ? `${evidenceDisplayName.plural} Table` : evidenceDisplayName.singular,
                getLocationLabel: (comment) =>
                    comment.location.type === 'table'
                        ? `${evidenceDisplayName.plural} Table`
                        : (comment.location.name ?? 'Deleted'),
                getLocationTarget: (comment, currentPath) => {
                    if (comment.location.type === 'table') return { pathname: '/evidence' }
                    if (comment.location.type !== 'item' || !comment.location.id) return null
                    return getAutoOpenTarget('/evidence', { _id: comment.location.id }, currentPath)
                },
            },
            {
                id: 'tpp',
                label: tppAcronymDisplayName,
                emptyStateTitle: `No ${tppAcronymDisplayName} comments yet`,
                emptyStateSubtitle: 'Comments added from TPP cells will appear here.',
                getTypeLabel: () => tppAcronymDisplayName,
                getLocationLabel: (comment) =>
                    comment.location.type === 'table'
                        ? `${tppAcronymDisplayName} Table`
                        : (comment.location.name ?? 'Deleted'),
                getLocationTarget: (comment) => {
                    if (comment.location.type === 'missing') return null
                    return { pathname: '/tpp' }
                },
            },
        ],
        [
            activitiesDisplayName.plural,
            activitiesDisplayName.singular,
            evidenceDisplayName.plural,
            evidenceDisplayName.singular,
            strategicImperativeDisplayName.plural,
            strategicImperativeDisplayName.singular,
            tppAcronymDisplayName,
            valueMessageDisplayName.plural,
            valueMessageDisplayName.singular,
            valueNarrativeDisplayName.singular,
        ]
    )

    const tabs = useMemo((): CommentsViewTab[] => {
        const tabsById = domainTabs.reduce<Partial<Record<CommentsViewDomainTabId, CommentsViewDomainTab>>>(
            (acc, tab) => {
                acc[tab.id] = tab
                return acc
            },
            {}
        )

        const allTab: CommentsViewTab = {
            id: 'all',
            label: 'All',
            emptyStateTitle: 'No comments yet',
            emptyStateSubtitle: 'Comments added across the workspace will appear here.',
            getTypeLabel: (dataType) =>
                getCommentsTabForDataType(dataType, tabsById)?.getTypeLabel(dataType) ?? 'Unknown',
            getLocationLabel: (comment) =>
                getCommentsTabForComment(comment, tabsById)?.getLocationLabel(comment) ??
                comment.location.name ??
                'Deleted',
            getLocationTarget: (comment, currentPath) =>
                getCommentsTabForComment(comment, tabsById)?.getLocationTarget(comment, currentPath) ?? null,
        }

        return [allTab, ...domainTabs]
    }, [domainTabs])

    const domainTabLabels = useMemo(
        () =>
            domainTabs.reduce<Record<CommentsViewDomainTabId, string>>(
                (acc, tab) => {
                    acc[tab.id] = singular(tab.label) + ' Filters'
                    return acc
                },
                {} as Record<CommentsViewDomainTabId, string>
            ),
        [domainTabs]
    )

    return { tabs, domainTabLabels }
}
